import React, { useState, useEffect } from "react";
import "./StaffManagements.css";
import { useNavigate } from "react-router-dom";
import AddStaffModal from "../AddStaffModal/AddStaffModal";
import ShadcnActionDropdown from "../common/ShadcnActionDropdown/ShadcnActionDropdown";
import {
  getStaffMembers,
  toggleStaffStatus,
  verifyStaffMember,
  deleteStaffMember,
} from "../../services/staffManagement";
import LoadingSpinner from "../common/LoadingSpinner/LoadingSpinner";

const StaffManagements = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staffMembers, setStaffMembers] = useState([]);

  // Fetch staff members on component mount
  useEffect(() => {
    fetchStaffMembers();
  }, []);

  const fetchStaffMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getStaffMembers();
      const list = Array.isArray(response)
        ? response
        : (response?.data || response || []);
      setStaffMembers(list || []);
    } catch (error) {
      console.error("Error fetching staff members:", error);
      setError("Failed to fetch staff members. Please check your connection and try again.");
      setStaffMembers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (newStaff) => {
    try {
      // Add to local state with proper ID generation
      const newStaffMember = {
        ...newStaff,
        id: `staff-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        verified: newStaff.verified || false,
        isBlocked: false,
        createdAt: new Date().toISOString(),
      };

      setStaffMembers([...staffMembers, newStaffMember]);
      setIsModalOpen(false);

      // Show success message
      alert("Staff member added successfully!");
    } catch (error) {
      console.error("Error adding staff member:", error);
      setError("Failed to add staff member. Please try again.");
    }
  };

  // Handle block/unblock staff member
  const handleToggleStatus = async (staffId, isBlocked) => {
    try {
      await toggleStaffStatus(staffId, isBlocked);
      setStaffMembers(staffMembers.map(staff =>
        staff.id === staffId ? { ...staff, isBlocked } : staff
      ));

      // Show success message
      alert(`Staff member ${isBlocked ? 'blocked' : 'unblocked'} successfully!`);
    } catch (error) {
      console.error("Error toggling staff status:", error);
      setError("Failed to update staff status. Please try again.");

      // Revert the change in UI if API call failed
      setStaffMembers(staffMembers.map(staff =>
        staff.id === staffId ? { ...staff, isBlocked: !isBlocked } : staff
      ));
    }
  };

  // Handle verify staff member
  const handleVerify = async (staffId) => {
    try {
      await verifyStaffMember(staffId);
      setStaffMembers(staffMembers.map(staff =>
        staff.id === staffId ? { ...staff, verified: true } : staff
      ));

      // Show success message
      alert("Staff member verified successfully!");
    } catch (error) {
      console.error("Error verifying staff member:", error);
      setError("Failed to verify staff member. Please try again.");
    }
  };

  // Handle delete staff member
  const handleDelete = async (staffId) => {
    if (window.confirm("Are you sure you want to delete this staff member? This action cannot be undone.")) {
      try {
        await deleteStaffMember(staffId);
        setStaffMembers(staffMembers.filter(staff => staff.id !== staffId));

        // Show success message
        alert("Staff member deleted successfully!");
      } catch (error) {
        console.error("Error deleting staff member:", error);
        setError("Failed to delete staff member. Please try again.");
      }
    }
  };

  return (
    <div className="staff-managements-container">
      <div className="staff-managements-header">
        <h1>Staff Management</h1>
        <button className="add-button" onClick={() => setIsModalOpen(true)}>
          + Add Staff
        </button>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <p style={{ fontSize: '0.9rem', color: '#6c757d', marginTop: '0.5rem' }}>
            This might be due to backend connectivity issues. Please ensure the backend server is running.
          </p>
          <button onClick={fetchStaffMembers} className="retry-button">
            Retry
          </button>
        </div>
      )}

      <div className="staff-count">Staff Members: {staffMembers.length}</div>

      {loading ? (
        <LoadingSpinner message="Loading staff members..." />
      ) : (
        <div className="table-container">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Profile</th>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Role</th>
                <th>Phone Number</th>
                <th>Email</th>
                <th>Verified</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staffMembers.map((staff) => (
                <tr key={staff.id}>
                  <td>
                    <div className="profile-image">
                      {staff.profileImage ? (
                        <img
                          src={staff.profileImage}
                          alt={`${staff.firstName}'s profile`}
                        />
                      ) : (
                        <div className="profile-placeholder"></div>
                      )}
                    </div>
                  </td>
                  <td>{staff.firstName}</td>
                  <td>{staff.lastName}</td>
                  <td>{staff.role}</td>
                  <td>{staff.phoneNumber}</td>
                  <td>{staff.email}</td>
                  <td>
                    <span
                      className={`verification-badge ${staff.verified ? "verified" : "not-verified"
                        }`}
                    >
                      {staff.verified ? "Verified" : "Not Verified"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`status-badge ${staff.isBlocked ? "blocked" : "active"
                        }`}
                    >
                      {staff.isBlocked ? "Blocked" : "Active"}
                    </span>
                  </td>
                  <td>
                    <ShadcnActionDropdown
                      size="small"
                      variant="default"
                      actions={[
                        {
                          label: staff.isBlocked ? 'Unblock' : 'Block',
                          icon: 'settings',
                          onClick: () => handleToggleStatus(staff.id, !staff.isBlocked),
                          tooltip: staff.isBlocked ? 'Unblock this staff member' : 'Block this staff member'
                        },
                        ...(!staff.verified ? [{
                          label: 'Verify',
                          icon: 'settings',
                          onClick: () => handleVerify(staff.id),
                          tooltip: 'Verify this staff member'
                        }] : []),
                        {
                          label: 'View Details',
                          icon: 'view',
                          onClick: () => navigate(`/staff-details/${staff.id}`),
                          tooltip: 'View staff details'
                        },
                        {
                          label: 'Delete',
                          icon: 'delete',
                          onClick: () => handleDelete(staff.id),
                          danger: true,
                          tooltip: 'Delete this staff member'
                        }
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <AddStaffModal
          onSave={handleAdd}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default StaffManagements;
